<script lang="ts">
  /**
   * The flyability outlook, over the map, while a flight is in the air.
   *
   * Four three-hour slots as coloured letters, which is how a GAFOR is read: you glance at it,
   * you see whether the next window is green, and you go back to flying.
   *
   * **It says "abgeleitet" on it, and that is not decoration.** This is not the DWD product —
   * see the daemon's `gafor.ts` for what it actually is and, more importantly, for the way its
   * cloud-base estimate errs. It errs optimistic. A pilot who mistook this for a briefing would
   * be mistaking it in the dangerous direction, so the label is part of the component and not a
   * detail that a later redesign gets to tidy away.
   */
  import type { GaforOutlook, GaforSlot } from './api.ts';

  interface Props {
    outlook: GaforOutlook | null;
    /** Shown while the first request is in flight, so the panel does not pop in. */
    loading?: boolean;
  }

  let { outlook, loading = false }: Props = $props();

  let open = $state(false);

  /**
   * Four hues that stay apart at 1.35 rem.
   *
   * D and M were a yellow and an orange half a step from each other, and at this size they read
   * as the same colour — which defeats a control whose whole job is to be understood at a
   * glance. Green, yellow, deep orange, red now step clearly, and the letter is on the chip in
   * any case for anybody who cannot separate them by hue at all.
   */
  const COLOURS: Record<GaforSlot['category'], string> = {
    O: '#5ad18f',
    D: '#ecc94b',
    M: '#f97316',
    X: '#e5544b',
    '?': '#6b7280',
  };

  const NAMES: Record<GaforSlot['category'], string> = {
    O: 'Oscar — offen',
    D: 'Delta — schwierig',
    M: 'Mike — marginal',
    X: 'X-Ray — geschlossen',
    '?': 'keine Modelldaten',
  };

  const hour = (t: number): string =>
    new Date(t * 1000).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const km = (m: number | null): string => (m === null ? '–' : `${Math.round(m / 1000)} km`);
  const ft = (v: number | null): string =>
    v === null ? 'keine Untergrenze' : `${v.toLocaleString('de-DE')} ft`;

  const detail = (s: GaforSlot): string =>
    `${NAMES[s.category]}\nSicht ${km(s.visibilityM)}, Basis ${ft(s.ceilingFt)}` +
    (s.limitedBy === null ? '' : `\nbegrenzt durch: ${s.limitedBy}`);
</script>

<div class="gafor" class:open>
  <button
    class="head"
    type="button"
    onclick={() => (open = !open)}
    aria-expanded={open}
    title="Abgeleitete Flugwetter-Einschätzung — kein DWD-GAFOR"
  >
    <span class="tag">GAFOR<span class="star">*</span></span>
    {#if loading && outlook === null}
      <span class="wait">…</span>
    {:else if outlook === null || outlook.slots.length === 0}
      <span class="wait">–</span>
    {:else}
      <span class="slots">
        {#each outlook.slots as s (s.fromT)}
          <span class="slot" style:--c={COLOURS[s.category]} title={detail(s)}>
            <span class="letter">{s.category}</span>
            <span class="time">{hour(s.fromT)}</span>
          </span>
        {/each}
      </span>
    {/if}
  </button>

  {#if open && outlook !== null}
    <div class="body">
      <p class="note">{outlook.note}</p>
      <dl>
        {#each outlook.slots as s (s.fromT)}
          <div>
            <dt style:--c={COLOURS[s.category]}>
              <span class="letter">{s.category}</span>
              {hour(s.fromT)}–{hour(s.toT)}
            </dt>
            <dd>{km(s.visibilityM)} · {ft(s.ceilingFt)}</dd>
          </div>
        {/each}
      </dl>
    </div>
  {/if}
</div>

<style>
  /* The same translucent-over-blur recipe as the map controls, so it belongs to the map rather
     than sitting on it. */
  .gafor {
    background: color-mix(in srgb, var(--surface-2, #161a22) 82%, transparent);
    backdrop-filter: blur(14px);
    border: 1px solid var(--line-strong);
    border-radius: 0.7rem;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    overflow: hidden;
  }

  .head {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
    border: 0;
    background: transparent;
    color: var(--text);
    font: inherit;
    padding: 0.38rem 0.6rem;
    cursor: pointer;
  }

  .tag {
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    color: var(--text-dim);
  }
  /* The asterisk is the "not the real thing" mark, and it is always on screen even when the
     panel is closed. */
  .star {
    color: var(--accent);
    margin-left: 1px;
  }

  .slots {
    display: flex;
    gap: 0.3rem;
  }
  .slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    min-width: 2.1rem;
  }
  .letter {
    display: grid;
    place-items: center;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 0.32rem;
    background: color-mix(in srgb, var(--c) 22%, transparent);
    border: 1px solid color-mix(in srgb, var(--c) 55%, transparent);
    color: var(--c);
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1;
  }
  .time {
    font-size: 0.58rem;
    color: var(--text-dim);
    font-variant-numeric: tabular-nums;
  }
  .wait {
    color: var(--text-dim);
    font-size: 0.8rem;
  }

  .body {
    border-top: 1px solid var(--line);
    padding: 0.5rem 0.6rem 0.6rem;
    max-width: 19rem;
  }
  .note {
    margin: 0 0 0.45rem;
    font-size: 0.68rem;
    line-height: 1.4;
    color: var(--text-dim);
  }
  .body dl {
    margin: 0;
    display: grid;
    gap: 0.25rem;
  }
  .body dl div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
  }
  .body dt {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    color: var(--text-dim);
  }
  .body dd {
    margin: 0;
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
  }
</style>
